import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { UserWithWorkspaceRole, WorkspaceRoleId } from '@robocorp/robocloud-internal-cds-sdk';
import { workspaceService } from '../../services/workspaceAPI';
import { organizationService } from '../../services/organizationAPI';
import { getWorkspacesByShortNameQueryKey, getWorkspacesQueryKey } from './useWorkspacesQuery';
import { useLazyQuery } from '../useLazyQuery';
import { formatErrorPayload, UseApiQueryOptions, validateApiResponse } from '../utils';

export enum WorkspaceMembersQueryErrors {
  GET_WORKSPACE_MEMBERS_ERROR = 'GET_WORKSPACE_MEMBERS_ERROR',
  REMOVE_WORKSPACE_MEMBER_ERROR = 'REMOVE_WORKSPACE_MEMBER_ERROR',
  SET_WORKSPACE_ROLE_ERROR = 'SET_WORKSPACE_ROLE_ERROR',
  ADD_ORG_MEMBER_TO_WORKSPACE_ERROR = 'ADD_ORG_MEMBER_TO_WORKSPACE_ERROR',
  LEAVE_WORKSPACE_ERROR = 'LEAVE_WORKSPACE_ERROR',
}

const ROOT_KEY = 'workspace_members';
export const getWorkspaceMembersQueryKey = (args: { workspaceId: string }) => [
  ROOT_KEY,
  args.workspaceId,
];

export const useWorkspaceMembersQuery = (
  workspaceId: string,
  options?: UseApiQueryOptions<typeof workspaceService.getWorkspaceMembers>,
) => {
  const dispatch = useDispatch();
  return useQuery(
    getWorkspaceMembersQueryKey({ workspaceId }),
    async () => {
      return validateApiResponse(await workspaceService.getWorkspaceMembers(workspaceId)).data;
    },
    {
      onError: (error: Error) =>
        dispatch(
          formatErrorPayload(error, {
            type: WorkspaceMembersQueryErrors.GET_WORKSPACE_MEMBERS_ERROR,
            organizationId: workspaceId,
            showSnackbar: false,
          }),
        ),
      ...options,
    },
  );
};

export function useWorkspaceMembersLazyQuery(workspaceId?: string) {
  const dispatch = useDispatch();
  return useLazyQuery<{ workspaceId: string }, UserWithWorkspaceRole[], Error>(
    (params) => getWorkspaceMembersQueryKey(params),
    (params) => async () => {
      const response = validateApiResponse(
        await workspaceService.getWorkspaceMembers(params.workspaceId),
      );
      return response.data || [];
    },
    {
      onError: (error: Error) =>
        dispatch(
          formatErrorPayload(error, {
            type: WorkspaceMembersQueryErrors.GET_WORKSPACE_MEMBERS_ERROR,
            organizationId: workspaceId,
            showSnackbar: false,
          }),
        ),
    },
    workspaceId ? { workspaceId } : undefined,
  );
}

export function useWorkspaceRemoveMemberMutation(workspaceId: string) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation(
    async ({ userId }: { userId: string }) => {
      const response = validateApiResponse(
        await workspaceService.removeWorkspaceMember(workspaceId, userId),
      );
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(getWorkspaceMembersQueryKey({ workspaceId }));
        queryClient.invalidateQueries(getWorkspacesByShortNameQueryKey());
        queryClient.invalidateQueries(getWorkspacesQueryKey());
      },
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(error, {
            type: WorkspaceMembersQueryErrors.REMOVE_WORKSPACE_MEMBER_ERROR,
          }),
        );
      },
    },
  );
}

export function useWorkspaceSetRoleMutation(workspaceId: string) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation<
    UserWithWorkspaceRole | undefined,
    Error,
    {
      userId: string;
      roleId: WorkspaceRoleId;
    },
    { previousMembers: UserWithWorkspaceRole[] | null }
  >(
    async ({ userId, roleId }) => {
      const response = validateApiResponse(
        await workspaceService.setWorkspaceRole(workspaceId, userId, roleId),
      );
      return response.data;
    },
    {
      onMutate: async ({ userId, roleId }) => {
        await queryClient.cancelQueries(getWorkspaceMembersQueryKey({ workspaceId }));

        const previousMembers =
          queryClient.getQueryData<UserWithWorkspaceRole[]>(
            getWorkspaceMembersQueryKey({ workspaceId }),
          ) ?? [];

        const idx = previousMembers.findIndex(({ id }) => id === userId);
        if (idx === -1) return { previousMembers: null };

        const next = [...previousMembers];
        const updated = {
          ...next[idx],
          workspaceRole: {
            ...next[idx].workspaceRole,
            id: roleId,
            name: roleId,
          },
        };
        next.splice(idx, 1, updated);

        queryClient.setQueryData(getWorkspaceMembersQueryKey({ workspaceId }), next);

        return { previousMembers };
      },
      onSuccess: () => {
        queryClient.invalidateQueries(getWorkspaceMembersQueryKey({ workspaceId }));
        queryClient.invalidateQueries(getWorkspacesByShortNameQueryKey());
        queryClient.invalidateQueries(getWorkspacesQueryKey());
      },
      onError: (error: Error, variables, context) => {
        dispatch(
          formatErrorPayload(error, {
            type: WorkspaceMembersQueryErrors.SET_WORKSPACE_ROLE_ERROR,
          }),
        );

        queryClient.setQueryData(
          getWorkspaceMembersQueryKey({ workspaceId }),
          context?.previousMembers,
        );
      },
    },
  );
}

export function useAddWorkspaceMemberMutation() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation(
    async ({
      userId,
      organizationId,
      workspaceId,
    }: {
      userId: string;
      organizationId: string;
      workspaceId: string;
    }) => {
      const response = validateApiResponse(
        await organizationService.addWorkspaceMember(organizationId, workspaceId, userId),
      );
      return response.data;
    },
    {
      onSuccess: (_, requestData) => {
        queryClient.invalidateQueries(getWorkspacesByShortNameQueryKey());
        queryClient.invalidateQueries(getWorkspacesQueryKey());
        queryClient.invalidateQueries(
          getWorkspaceMembersQueryKey({ workspaceId: requestData.workspaceId }),
        );
      },
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(error, {
            type: WorkspaceMembersQueryErrors.ADD_ORG_MEMBER_TO_WORKSPACE_ERROR,
          }),
        );
      },
    },
  );
}

export function useLeaveWorkspaceMutation() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation(
    async ({ workspaceId, userId }: { workspaceId: string; userId: string }) => {
      const response = validateApiResponse(
        await workspaceService.removeWorkspaceMember(workspaceId, userId),
      );
      return response.data;
    },
    {
      onSuccess: (data) => {
        if (!data) return;
        queryClient.invalidateQueries(getWorkspacesQueryKey());
        queryClient.invalidateQueries(getWorkspacesByShortNameQueryKey());
        queryClient.invalidateQueries(
          getWorkspaceMembersQueryKey({ workspaceId: data.workspace_id }),
        );
      },
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(error, {
            type: WorkspaceMembersQueryErrors.LEAVE_WORKSPACE_ERROR,
          }),
        );
      },
    },
  );
}
